import api from '@/api'
import invoices from '@/api/apiSferest/services/invoices'
import i18n from '@/i18n'
import session from '@/auth/session'
import { apificateDate } from '@/helpers/formatDate'
import { statusByType } from '@/utils/invoiceStatus'
import { getUserSubscription, getPromoterCode } from '@/utils/tokenUtils'
import store from '@/store'

/**
 * Maps invoice to usable object
 * @param {*} id  Identifier
 * @param {*} createDate Date
 * @param {*} issueDate Date
 * @param {*} invoiceId Identifier
 * @param {*} provider String
 * @param {*} client String
 * @param {*} agent String
 * @param {*} taxes String
 * @param {*} taxableAmount Number
 * @param {*} amount Number
 * @param {*} status String
 * @param {*} statusCode Number
 * @param {*} type String
 * @param {*} subtype String
 * @param {*} hasAttachments Boolean
 * @param {*} origin String
 * @param {*} financial Boolean
 * @param {*} format String
 * @param {*} rectified Boolean
 * @return {Object} Mapped info
 */
const mapInvoiceData = ({ id, createDate, issueDate, invoiceId, provider, client, agent, taxes, taxableAmount, amount, status, statusCode, type, subtype, hasAttachments, origin, financial, format, rectified }) => {
  let providerDocument = provider ? provider.document : null
  return {
    id,
    createDate,
    issueDate,
    invoiceId,
    serie: invoiceId.serie,
    number: invoiceId.number,
    provider,
    agent,
    client,
    taxes,
    taxableAmount,
    amount,
    type,
    subtype,
    status,
    statusCode,
    hasAttachments,
    providerDocument,
    origin,
    financial,
    format,
    rectified
  }
}

/**
 * Loads all invoices
 * @param {*} commit Method
 * @param {*} dispatch Method
 * @param {*} type  Invoice type
 * @return {Promise} Wrapper
 */
export const load = ({ commit, dispatch }, type) => {
  let selectorsType = _.isObject(type) ? type.selectors : type
  let operationType = _.isObject(type) ? type.operation : type
  return new Promise((resolve) => {
    commit('SET_TYPE', operationType)
    commit('SET_LOADED', false)
    let proc = [
      dispatch('list/unselect'),
      dispatch('list/emptyList'),
      dispatch('resetParams')
    ]
    if (type === 'asProviderAndPayer') proc.push(dispatch('selectors/fetchStatus', 'asProvider', { root: true }))
    else if (type === 'old') proc.push(dispatch('selectors/fetchStatus', 'asProvider', { root: true }))
    else if (type === 'financeables') proc.push(dispatch('selectors/fetchStatus', 'factoring', { root: true }))
    else if (type !== 'thirdPartyAttachable') proc.push(dispatch('selectors/fetchStatus', selectorsType, { root: true }))
    // TODO Remove when is created selector
    Promise.all(proc)
    .then(() => {
      dispatch('getList').then(() => {
        commit('SET_LOADED', true)
        resolve()
      }, err => {
        commit('SET_LOADED', true)
        reject(new Error(err))
      })
      .catch((err) => {
        console.log(err)
      })
    })
  })
}

/**
 * Returns invoices list
 * @param {*} commit Method
 * @param {*} state Object
 * @param {*} getters Object
 * @param {*} dispatch Method
 * @return {Promise} Wrapper
 */
export const getList = ({ commit, state, getters, dispatch }) => {
  return new Promise((resolve, reject) => {
    dispatch('search/searching')
    dispatch('loading')
    let params = {
      ...getters['query'],
      type: state.search.type,
      role: state.search.role,
      sort: state.search.sort,
      limit: state.search.limit
    }
    let statusList = []
    if (state.type !== 'drafts' && state.type !== 'thirdPartyDrafts' && state.type !== 'thirdParty') {
      statusList.push(-1)
    }
    statusList.push(statusByType[state.type])
    let isStatusPermitted = false
    if (!params.status) {
      params.status = statusList
      isStatusPermitted = true
    } else {
      statusList.forEach((statusOne) => {
        if (parseInt(state.status) === statusOne) {
          isStatusPermitted = true
        }
      })
      if (!isStatusPermitted) {
        reject(new Error('En esta pantalla, no se pueden mostrar facturas de ese estado'))
      }
    }
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null) params[key] = undefined
    })
    params.from = apificateDate(params.from)
    params.to = apificateDate(params.to)
    invoices.list[state.type](params)
      .then((res) => {
        if (!Array.isArray(res.data)) reject(new Error('Response must be an array'))
        commit('search/SET_MORE', res.headers['x-next-page'])
        commit('list/SET_LIST', res.data.map(invoice => mapInvoiceData(invoice)))
        dispatch('loaded')
        dispatch('search/searched')
        .then(() => {
          resolve(res.data)
        })
      }).catch(() => {
        dispatch('loaded')
        dispatch('search/searched')
        .then(() => {
          resolve([])
        })
      })
  })
}

/**
 * Loads more invoices
 * @param {*} commit  Method
 * @param {*} state Object
 * @param {*} dispatch  Method
 * @return {Promise} Wrapper
 */
export const getMore = ({ commit, state, dispatch }) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      api.apiSferest.get(state.search.more, { data: {} })
      .then((res) => {
        if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
          reject(new Error('Error de conexión'))
          return
        }
        commit('search/SET_MORE', res.headers['x-next-page'])
        commit('list/ADD_TO_LIST', res.data.map(invoice => mapInvoiceData(invoice)))
        resolve(res.data)
      }, () => {
        reject(new Error('Error de conexión'))
      })
    })
  })
}

/**
 * Removes an invoice
 * @param state Object
 * @param commit Method
 * @param getters Object
 * @param dispatch Method
 * @return {Promise} Wrapper
 */
export const remove = ({ state, commit, getters, dispatch }) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let params = {
        id: getters['getSelectedIds']
      }
      invoices.remove[state.type](params)
      .then((res) => {
        resolve(res)
      })
    })
  })
}

/**
 * Resets query params
 * @param commit Method
 * @param dispatch Method
 * @return {Promise} Wrapper
 */
export const resetParams = ({ commit, dispatch }) => {
  return new Promise((resolve, reject) => {
    commit('SET_DATE_TO', undefined)
    commit('SET_DATE_FROM', undefined)
    commit('SET_SERIE', undefined)
    commit('SET_NUMBER', undefined)
    commit('SET_DOCUMENTPROVIDER', undefined)
    commit('SET_DOCUMENTCLIENT', undefined)
    commit('SET_AMOUNT', undefined)
    commit('SET_STATUS', -1)
    commit('SET_ID', undefined)
    commit('SET_INVOICEID', undefined)
    commit('search/SET_LIMIT', 10)
    commit('search/SET_MORE', undefined)
    commit('search/SET_SORT', undefined)
    commit('SET_NEWSTATUS', undefined)
    resolve()
  })
}

/**
 * Rejects an invoice
 * @param state Object
 * @param commit Method
 * @param getters Object
 * @param dispatch Method
 * @param comments Invoice comments
 * @return {Promise} Wrapper
 */
export const reject = ({ state, commit, getters, dispatch }, comments) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let params = {
        id: getters['getSelectedIds']
      }
      let body = {
        status: 'reject',
        comments
      }
      invoices.submit[state.type](body, params)
      .then((res) => {
        resolve(res)
      })
    })
  })
}

/**
 * Unrejects an invoice
 * @param state Object
 * @param commit Method
 * @param getters Object
 * @param dispatch Method
 * @param comments Invoice comments
 * @param newstatus New Invoice Status
 * @return {Promise} Wrapper
 */
export const unreject = ({ state, commit, getters, dispatch }, comments) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let params = {
        newStatus: state.newStatus,
        id: getters['getSelectedIds']
      }
      let body = {
        comments,
        status: 'unreject'
      }
      invoices.unreject[state.type](body, params)
      .then((res) => {
        resolve(res)
      })
    })
  })
}

/**
 * Send a request to cancel an invoice
 * @param state Object
 * @param commit Method
 * @param getters Object
 * @param dispatch Method
 * @param comments Object Reason why the invoice is needed to be cancelled 
 * @return {Promise} Wrapper
 */
export const cancelInvoice = ({ state, commit, getters, dispatch }, comments) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let params = {
        id: getters['getSelectedIds']
      }
      let body = {
        comments,
        status: 'cancel'
      }
      invoices.cancelInvoice[state.type](body, params)
      .then((res) => {
        resolve(res)
      })
    })
  })
}

/**
 * Send a request to cancel an invoice
 * @param state Object
 * @param commit Method
 * @param getters Object
 * @param dispatch Method
 * @param comments Object Reason why the invoice is needed to be cancelled 
 * @param code Object Code for new status
 * @return {Promise} Wrapper
 */
export const manageCancelInvoice = ({ state, commit, getters, dispatch }, payload) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let params = {
        id: getters['getSelectedIds'],
        code: payload.code
      }
      let body = {
        comments: payload.comments
      }
      invoices.manageCancelInvoice[state.type](body, params)
      .then((res) => {
        resolve(res)
      })
    })
  })
}

/**
 * Accepts a new invoice
 * @param state Object
 * @param commit Method
 * @param getters Object
 * @param dispatch Method
 * @return {Promise} Wrapper
 */
export const accept = ({ state, commit, getters, dispatch }) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let params = {
        id: getters['getSelectedIds']
      }
      let body = {
        status: 'accept'
      }
      invoices.submit[state.type](body, params)
      .then((res) => {
        resolve(res)
      })
    })
  })
}

export const issue = ({ state, commit, getters, dispatch }, { force }) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, { root: true })
    .then(() => {
      let params = {
        id: getters['getSelectedIds']
      }

      let body = {
        status: 'issue',
        force
      }

      if (session.isApiLogin() && !session.isSupportSession()) {
        store.dispatch('me/get').then(() => {
          var issuedInvoices = store.state.me.issuedInvoices
          var maxFreeIssuedInvoices = store.state.me.maxIssuedInvoices
          var numberInvoicesToIssue = maxFreeIssuedInvoices - issuedInvoices
          if (getPromoterCode() === '9900' && getUserSubscription() === 'FREE' && issuedInvoices >= maxFreeIssuedInvoices) {
            return dispatch('errors/addMaxFreeIssuedError', { msg: i18n.tc('invoices.issue.errors.maxInvoicesIssued', 2, [maxFreeIssuedInvoices]) }, { root: true })
          } else if (getPromoterCode() === '9900' && getUserSubscription() === 'FREE' && params.id.length > numberInvoicesToIssue) {
            return dispatch('errors/addMaxFreeIssuedError', { msg: i18n.tc('invoices.issue.errors.maxInvoicesIssued', 0, [issuedInvoices, numberInvoicesToIssue]) }, { root: true })
          }
          return dispatchSaveIssueInvoice(resolve, reject, commit, state, dispatch, params, body)
        })
      } else {
        return dispatchSaveIssueInvoice(resolve, reject, commit, state, dispatch, params, body)
      }
    })
  })
}

/**
 * Saves an issue from a invoice
 * @param resolve Promise
 * @param reject Promise
 * @param commit Method
 * @param state Object
 * @param dispatch Method
 * @param params Object
 * @param body Object
 */
function dispatchSaveIssueInvoice(resolve, reject, commit, state, dispatch, params, body) {
  invoices.submit[state.type](body, params)
  .then((res) => {
    resolve(res)
  }, err => {
      /*
      When response is 303 (See_Other), user is routed to provided assets info.
      Note: 304 code cannot be used because CORS is not accepted.
      */
    if (err.request && err.request.status === 303) {
      let q = new URLSearchParams(err.request.responseURL)
      let id = q.get('id')
      let invoiceType = state.type
      Promise.all([
        dispatch('invoice/edit', { id, invoiceType }, { root: true }),
        dispatch('selectors/fetchStatus', invoiceType, { root: true })
      ]).then(() => {
        commit('invoice/SET_SHOWINVOICEASSETS', true, { root: true })
        resolve(false)
      })
    } else reject(err)
  })
}

export const download = ({ state, commit, getters, dispatch }, { format, id, isTbai }) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let params = {
        id: id || getters['getSelectedIds'],
        format: format,
        isTbai: isTbai
      }
      invoices.download[state.type](params)
      .then((res) => {
        resolve(res)
      }, (err) => {
        reject(err)
      })
    })
  })
}
export const financing = ({ dispatch }, financingObject) => {
  return new Promise((resolve, reject) => {
    let agreementId = financingObject.agreement.id
    let expedientId = financingObject.agreement.expedient
    if (agreementId) {
      let obj = {
        agreementId: agreementId,
        password: financingObject.password,
        expedientId: expedientId
      }
      dispatch('discounts', obj)
      .then((res) => {
        resolve(res)
      }, error => {
        reject(error)
      })
    } else {
      let obj = {
        agreement: financingObject.agreement,
        password: financingObject.password
      }
      dispatch('factoring', obj)
      .then((res) => {
        resolve(res)
      })
    }
  })
}

export const discounts = ({ state, commit, getters, dispatch }, financingObject) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let selectedInvoices = getters['list/getSelectedItems']
      let throwError = selectedInvoices.find(x => x.financial === 'factoring')
      if (throwError) {
        let msg = i18n.t('invoices.confirm.financeables.errorDiscount')
        dispatch('errors/addValidationError', { msg }, { root: true })
        return
      }
      let body = {
        invoices: getters['getSelectedIds'],
        agreementId: financingObject.agreementId,
        password: financingObject.password,
        expedientId: financingObject.expedientId
      }
      invoices.solicitude['discounts'](body, {})
      .then((res) => {
        resolve(res.data)
      },
      error => {
        reject(error)
      })
    })
  })
}

export const factoring = ({ state, commit, getters, dispatch }, financingObject) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let selectedInvoices = getters['list/getSelectedItems']
      let throwError = selectedInvoices.find(x => x.statusCode === 'discounts')
      if (throwError) {
        let msg = i18n.t('invoices.confirm.financeables.errorFactoring')
        dispatch('errors/addValidationError', { msg }, { root: true })
        return
      }
      let body = {
        invoices: getters['getSelectedIds'],
        expedientId: financingObject.agreement.expedient,
        password: financingObject.password
      }
      invoices.solicitude['factoring'](body, {})
      .then((res) => {
        resolve(res.data)
      })
    })
  })
}

export const getHistory = ({ commit, state, dispatch }, id) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      invoices.history(id)
      .then((res) => {
        commit('SET_HISTORY', { key: id, value: res.data })
        resolve(res)
      })
    })
  })
}

const invoiceCSV = (json, headers) => {
  return json.reduce((acc, row) => {
    return acc + '\n' + headers.reduce((acc, col) => {
      let value = row[col] ? row[col] : ''
      if (col === 'invoiceId' && value) {
        value = value.serie
                  ? value.number
                    ? `${value.serie}${value.number}`
                    : `${value.serie}`
                  : value.number
                    ? `${value.number}`
                    : ''
      } else if (col === 'provider.name') value = row['provider'] ? row['provider'].name : ''
      else if (col === 'provider.document') value = row['provider'] ? row['provider'].document : ''
      else if (col === 'client.name') value = row['client'] ? row['client'].name : ''
      else if (col === 'client.document') value = row['client'] ? row['client'].document : ''
      else if (col === 'agent.name') value = row['agent'] ? row['agent'].name : ''
      else if (col === 'agent.document') value = row['agent'] ? row['agent'].document : ''
      return `${acc}${value};`
    }, '')
  }, '')
}

/**
 * Adds CSV from page
 * @param result Obtained
 * @param nextPage Number
 * @param headers Object
 * @return {Promise} Wrapper
 */
function addPagestoCSV (result, nextPage, headers) {
  return new Promise((resolve, reject) => {
    if (nextPage) {
      api.apiSferest.get(nextPage, { data: {} })
        .then((res) => {
          if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
            reject(new Error('Error de conexión'))
            return
          }
          result = result.concat(invoiceCSV(res.data, headers))
          nextPage = res.headers['x-next-page']
          addPagestoCSV(result, nextPage, headers).then(() => {
            resolve()
          })
        }, () => {
          reject(new Error('Error de conexión'))
        })
    } else {
      let csv = headers.reduce((acc, header) => {
        let i18header = i18n.t(`invoices.csv.${header}`).toUpperCase()
        return `${acc}${i18header};`
      }, '') + result
      downloadCSV(csv)
      resolve()
    }
  })
}

const downloadCSV = (csv) => {
  var link = document.createElement('a')
  link.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(csv))
  link.setAttribute('download', 'facturas.csv')
  document.body.appendChild(link)
  link.click()
}

export const generateCSV = ({ commit, state, getters, dispatch }) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      let headers = [
        'invoiceId',
        'createDate',
        'incomeDate',
        'issueDate',
        'status'
      ]
      headers = [ ...headers, 'provider.document', 'provider.name' ]
      if (state.type === 'thirdParty') headers = [ ...headers, 'agent.document', 'agent.name' ]
      headers = [ ...headers, 'client.document', 'client.name' ]
      headers = [
        ...headers,
        'taxableAmount',
        'taxes',
        'amount'
      ]

      let selection = getters['list/getSelectedItems']
      if (selection.length > 0) {
        let csv = headers.reduce((acc, header) => {
          let i18header = i18n.t(`invoices.csv.${header}`).toUpperCase()
          return `${acc}${i18header};`
        }, '') + invoiceCSV(selection, headers)
        downloadCSV(csv)
        resolve()
      } else {
        let params = {
          ...getters['query'],
          type: state.search.type,
          role: state.search.role,
          sort: state.search.sort,
          limit: 100
        }
        params.from = apificateDate(params.from)
        params.to = apificateDate(params.to)
        invoices.list[state.type](params).then((res) => {
          var result = invoiceCSV(res.data, headers)
          var nextPage = res.headers['x-next-page']
          addPagestoCSV(result, nextPage, headers).then(() => {
            resolve()
          })
        })
      }
    })
  })
}

export const loading = ({ commit }) => {
  return new Promise((resolve) => {
    commit('SET_LOADED', false)
    resolve(false)
  })
}

export const loaded = ({ commit }) => {
  return new Promise((resolve) => {
    commit('SET_LOADED', true)
    resolve(true)
  })
}

/* export const getSiiInvoices = ({ commit, state, dispatch }, siiType) => {
  return new Promise((resolve, reject) => {
    dispatch('errors/empty', {}, {root: true})
    .then(() => {
      invoices.getSiiInvoices(siiType)
      .then((res) => {
        commit('SET_SIIINVOICES', { key: siiType, value: res.data })
        resolve(res)
      })
    })
  })
} */
